import { metricDistance } from '../navigation/index.ts';
import type { CombatState } from '../combat/index.ts';

/**
 * Danger evaluation. Integer-only: every live enemy contributes its attack
 * damage scaled by proximity, plus the companion's own missing HP. No random
 * input, no render state — the same tactical state always yields the same level.
 */
export type DangerReport = Readonly<{
  level: number;
  enemies: number;
  nearestDistance: number | null;
}>;

/** World units inside which an enemy counts toward danger. */
export const DANGER_RANGE = 8000;

export function assessDanger(state: Readonly<CombatState>, actorId: string): DangerReport {
  const self = state.actors[actorId];
  if (!self) throw new Error(`Unknown companion ${actorId}`);
  let level = Math.max(0, self.maxHp - self.hp);
  let enemies = 0;
  let nearest: number | null = null;
  for (const id of Object.keys(state.actors).sort()) {
    if (id === actorId) continue;
    const other = state.actors[id]!;
    if (other.role !== 'enemy' || other.status === 'dead' || state.tactics?.actors[id]?.escaped) continue;
    const distance = metricDistance(self.position, other.position);
    if (distance === null) continue;
    if (nearest === null || distance < nearest) nearest = distance;
    enemies += 1;
    if (distance > DANGER_RANGE) continue;
    level += Math.floor((other.attackDamage * (DANGER_RANGE - distance)) / DANGER_RANGE);
  }
  return { level, enemies, nearestDistance: nearest };
}
